import type { FC } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Logger } from '@feature-hub/core';
import { RenderMode } from '@oneaudi/render-mode-service';
import { normalizeContentService } from '../../../content/editor';
import {
  ContentService,
  ExtendedGfaLocaleServiceV1,
  I18NService,
  IFeatureAppContent,
  IFeatureAppServices,
  RenderModeService,
  TrackingService,
} from '../../../content/feature-service-types';

export interface FalconContextProviderProps {
  children: React.ReactNode;
  readonly contentService: ContentService;
  readonly logger: Logger;
  readonly trackingService: TrackingService | undefined;
  readonly renderModeService?: RenderModeService;
  readonly localeService: ExtendedGfaLocaleServiceV1;
  readonly i18nService?: I18NService;
}

export const ContentContext = createContext<IFeatureAppServices | undefined>(undefined);

export const FalconContextProvider: FC<FalconContextProviderProps> = ({
  children,
  logger,
  trackingService,
  contentService,
  renderModeService,
  localeService,
  i18nService,
}) => {
  const [cmsContent, setCmsContent] = useState<IFeatureAppContent>(() =>
    normalizeContentService(contentService),
  );

  useEffect(() => {
    const contentChangeHandler = () => {
      setCmsContent(normalizeContentService(contentService));
    };

    contentService.onContentChange(contentChangeHandler);

    return () => {
      contentService.removeOnContentChange(contentChangeHandler);
    };
  }, [contentService]);

  const renderMode = useMemo(() => {
    const mode = renderModeService?.getRenderMode();
    return mode === RenderMode.EDIT ? RenderMode.EDIT : RenderMode.DEFAULT;
  }, [renderModeService]);

  const contextValue = useMemo(
    () => ({
      logger,
      trackingService,
      cmsContent,
      renderMode,
      localeService,
      i18nService,
    }),
    [logger, trackingService, cmsContent, renderMode, localeService, i18nService],
  );

  return <ContentContext.Provider value={contextValue}>{children}</ContentContext.Provider>;
};

export const useLogger = () => useContext(ContentContext)?.logger;

export function useFalconContext(): IFeatureAppServices {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error(
      'The context was not defined. Did you forget to wrap the feature app in <FalconContextProvider>?',
    );
  }
  return context;
}
